import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { UserCog, Trash2, Shield, Edit, Link, Unlink } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ConfirmDialog } from '@/components/ConfirmDialog';

const Users = () => {
  const { user: currentUser, setUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleFormData, setRoleFormData] = useState({
    role: '',
    linked_collaborator_id: '',
  });
  const [selectedCollaboratorId, setSelectedCollaboratorId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, collabsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/collaborators`)
      ]);
      setUsers(usersRes.data);
      setCollaborators(collabsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    setDeleteUserId(userId);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteUserId) return;
    try {
      await axios.delete(`${API}/users/${deleteUserId}`);
      toast.success('Utilisateur supprimé avec succès');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setConfirmDeleteOpen(false);
      setDeleteUserId(null);
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setRoleFormData({
      role: user.role,
      linked_collaborator_id: user.linked_collaborator_id || '',
    });
    setRoleDialogOpen(true);
  };

  const handleRoleUpdate = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.put(`${API}/users/${selectedUser.id}/role`, roleFormData);
      toast.success('Rôle mis à jour avec succès');
      setRoleDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour du rôle');
    }
  };

  const openLinkDialog = (user) => {
    setSelectedUser(user);
    setSelectedCollaboratorId(user.linked_collaborator_id || '');
    setLinkDialogOpen(true);
  };

  const handleLinkCollaborator = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await axios.put(`${API}/users/${selectedUser.id}/link-collaborator`, {
        role: selectedUser.role,
        linked_collaborator_id: selectedCollaboratorId || null,
      });
      
      // If linking current user, update the auth context
      if (selectedUser.id === currentUser?.id) {
        setUser(response.data);
      }
      
      toast.success(selectedCollaboratorId ? 'Profil collaborateur lié avec succès' : 'Liaison supprimée avec succès');
      setLinkDialogOpen(false);
      setSelectedUser(null);
      setSelectedCollaboratorId('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la liaison');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      admin: { label: 'Administrateur', variant: 'default' },
      manager: { label: 'Gestionnaire', variant: 'secondary' },
      collaborator: { label: 'Collaborateur', variant: 'outline' },
      client: { label: 'Client', variant: 'outline' },
    };
    const roleInfo = roleMap[role] || { label: role, variant: 'outline' };
    return <Badge variant={roleInfo.variant}>{roleInfo.label}</Badge>;
  };

  const getLinkedCollaboratorName = (linkedId) => {
    if (!linkedId) return null;
    const collab = collaborators.find(c => c.id === linkedId);
    return collab ? collab.name : 'Inconnu';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">
          Gestion des Utilisateurs
        </h1>
        <p className="text-muted-foreground mt-2">
          Gérez les utilisateurs et leurs rôles
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.map((user) => (
          <Card
            key={user.id}
            data-testid={`user-card-${user.id}`}
            className={`hover:shadow-lg transition-shadow duration-200 ${
              user.id === currentUser?.id ? 'border-primary border-2' : ''
            }`}
          >
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-primary text-primary-foreground text-lg">
                      {user.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg font-heading">{user.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-muted-foreground" />
                {getRoleBadge(user.role)}
                {user.id === currentUser?.id && (
                  <Badge variant="outline">Vous</Badge>
                )}
              </div>

              {user.linked_collaborator_id && (
                <div className="text-xs text-muted-foreground bg-muted p-2 rounded flex items-center gap-2">
                  <Link className="w-3 h-3" />
                  Profil lié: <span className="font-medium">{getLinkedCollaboratorName(user.linked_collaborator_id)}</span>
                </div>
              )}

              <div className="text-xs text-muted-foreground">
                Créé le {formatDate(user.created_at)}
              </div>

              {/* Boutons pour l'utilisateur actuel (admin) */}
              {user.id === currentUser?.id && user.role === 'admin' && (
                <div className="flex gap-2">
                  <Button
                    data-testid={`link-collaborator-${user.id}`}
                    onClick={() => openLinkDialog(user)}
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-full"
                  >
                    {user.linked_collaborator_id ? (
                      <>
                        <Unlink className="w-3 h-3 mr-2" />
                        Modifier liaison
                      </>
                    ) : (
                      <>
                        <Link className="w-3 h-3 mr-2" />
                        Lier un profil
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Boutons pour les autres utilisateurs */}
              {user.id !== currentUser?.id && (
                <div className="flex gap-2">
                  <Button
                    data-testid={`edit-role-${user.id}`}
                    onClick={() => openRoleDialog(user)}
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-full"
                  >
                    <Edit className="w-3 h-3 mr-2" />
                    Modifier rôle
                  </Button>
                  <Button
                    data-testid={`delete-user-${user.id}`}
                    onClick={() => handleDelete(user.id)}
                    variant="destructive"
                    size="sm"
                    className="rounded-full"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {users.length === 0 && (
        <div className="text-center py-12">
          <UserCog className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
        </div>
      )}

      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="font-heading text-base">Note sur les rôles</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p><strong>Administrateur:</strong> Accès complet, peut modifier et supprimer tous les éléments</p>
          <p><strong>Gestionnaire:</strong> Accès en lecture seule, peut consulter mais ne peut pas modifier</p>
          <p><strong>Collaborateur:</strong> Ne voit que ses propres outils et informations</p>
          <p><strong>Client:</strong> Accès uniquement à la section Location (emprunt et retour)</p>
        </CardContent>
      </Card>

      {/* Role Edit Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={(open) => {
        setRoleDialogOpen(open);
        if (!open) {
          setSelectedUser(null);
          setRoleFormData({ role: '', linked_collaborator_id: '' });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier le rôle</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-lg font-heading font-medium">{selectedUser.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Rôle</Label>
                <Select
                  value={roleFormData.role}
                  onValueChange={(value) => setRoleFormData({ ...roleFormData, role: value, linked_collaborator_id: value === 'collaborator' ? roleFormData.linked_collaborator_id : '' })}
                >
                  <SelectTrigger id="role" data-testid="role-select" className="rounded-lg">
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrateur</SelectItem>
                    <SelectItem value="manager">Gestionnaire</SelectItem>
                    <SelectItem value="collaborator">Collaborateur</SelectItem>
                    <SelectItem value="client">Client (Location uniquement)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {roleFormData.role === 'collaborator' && (
                <div className="space-y-2">
                  <Label htmlFor="linked_collaborator">Collaborateur lié *</Label>
                  <Select
                    value={roleFormData.linked_collaborator_id}
                    onValueChange={(value) => setRoleFormData({ ...roleFormData, linked_collaborator_id: value })}
                    required
                  >
                    <SelectTrigger id="linked_collaborator" data-testid="linked-collaborator-select" className="rounded-lg">
                      <SelectValue placeholder="Sélectionner un collaborateur" />
                    </SelectTrigger>
                    <SelectContent>
                      {collaborators.map((collab) => (
                        <SelectItem key={collab.id} value={collab.id}>
                          {collab.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Cet utilisateur ne pourra voir que les outils de ce collaborateur
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={() => setRoleDialogOpen(false)}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  Annuler
                </Button>
                <Button
                  data-testid="confirm-role-button"
                  onClick={handleRoleUpdate}
                  className="flex-1 rounded-full"
                  disabled={roleFormData.role === 'collaborator' && !roleFormData.linked_collaborator_id}
                >
                  Confirmer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link Collaborator Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={(open) => {
        setLinkDialogOpen(open);
        if (!open) {
          setSelectedUser(null);
          setSelectedCollaboratorId('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Link className="w-5 h-5" />
              Lier un profil collaborateur
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-lg font-heading font-medium">{selectedUser.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                <Badge variant="default" className="mt-2">
                  {selectedUser.role === 'admin' ? 'Administrateur' : selectedUser.role}
                </Badge>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  En liant un profil collaborateur à votre compte admin, vous pourrez voir vos propres outils 
                  tout en conservant tous vos droits d'administration.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="link_collaborator">Profil collaborateur</Label>
                <Select
                  value={selectedCollaboratorId || "none"}
                  onValueChange={(value) => setSelectedCollaboratorId(value === "none" ? "" : value)}
                >
                  <SelectTrigger id="link_collaborator" data-testid="link-collaborator-select" className="rounded-lg">
                    <SelectValue placeholder="Sélectionner un collaborateur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun (supprimer la liaison)</SelectItem>
                    {collaborators.map((collab) => (
                      <SelectItem key={collab.id} value={collab.id}>
                        {collab.name} {collab.email ? `(${collab.email})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {selectedCollaboratorId 
                    ? "Vous verrez les outils affectés à ce collaborateur dans votre tableau de bord"
                    : "Sélectionnez un profil ou laissez vide pour supprimer la liaison"
                  }
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => setLinkDialogOpen(false)}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  Annuler
                </Button>
                <Button
                  data-testid="confirm-link-button"
                  onClick={handleLinkCollaborator}
                  className="flex-1 rounded-full"
                >
                  {selectedCollaboratorId ? (
                    <>
                      <Link className="w-4 h-4 mr-2" />
                      Lier le profil
                    </>
                  ) : (
                    <>
                      <Unlink className="w-4 h-4 mr-2" />
                      Supprimer la liaison
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        onConfirm={confirmDelete}
        title="Supprimer l'utilisateur"
        description="Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible. L'utilisateur perdra tout accès à l'application."
      />
    </div>
  );
};

export default Users;